import React, {useState, useCallback, useRef} from 'react';
import base_url from '../baseUrls';
import {useSelector} from 'react-redux';
import {useGetRole} from './useGetRole';
import {useGetMyCompanyId} from './useGetMyCompanyId';
import usePagination from './usePagination';
import * as allConstants from '../constants/allConstants';
import BlockedPage from '../pages/misc/blockedCompany';
import AccessDenied from '../pages/misc/accessDenied';
import 'ag-grid-enterprise';
import axios from 'axios';
import ApiUtils from '../utils/apiUtils';
import Utils from '../utils/utils';
import cloneDeep from 'lodash/cloneDeep';
import {applyConvertToBooleanSetFilters, applySetFilterValueTransforms, resolveColumnDefsForFilter} from '../utils/gridFilterModelUtils';

/** Rows requested per page while building a direct ("All Pages") excel export. */
const EXPORT_PAGE_SIZE = 300;
/** Hard stop on the paging loop so a bad/missing totalRecords can never spin forever. */
const EXPORT_MAX_PAGES = 200;
/** Export requests have no server-side timeout; without this a hung request left the spinner running. */
const EXPORT_REQUEST_TIMEOUT_MS = 180000;

const useGridViewState = ()=> {
  const user = useSelector((state=> state.user));
  const company = useSelector((state=> state.company));  
  const role = useGetRole();
  const companyId = useGetMyCompanyId();
  const [fetching, setFetching] = useState(true);
  const [grid, setGrid] = useState();
  const [tempGrid, setTempGrid] = useState();  
  const [fetchParams, setFetchParams] = useState(null); 
  const [currUrlSearchParams, ] = useState(null);
  const [blocked, setBlocked] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [isLoading, setIsLoading] = useState(false); 
  const [customViewData, setCustomViewData] = useState({});
  const [newCustomViewId, setNewCustomViewId] = useState('');
  const [globalFilters, setGlobalFilters] = useState({}); 
  const [globalSearchFilters, setGlobalSearchFilters] = useState({});
  const isExportingViaEmail = useRef(false);

  const pagination = usePagination();
  const onFetchFail = (error) => {
    setFetching(false);
    if(error?.response?.data?.errorCode === allConstants.ERROR_CODES.COMPANY_BLOCKED) {
      setBlocked(true);
    }
  };
  const setTempGridToParent = (data, inFetchParams) => {
    setTempGrid(data);
    setFetchParams(inFetchParams);
    //setIsLoading(true);
  };

  const setGridToParent = (data)=> {
    setGrid(data);

    data.api.setSideBar({
      toolPanels: [
        {
          id: 'columns',
          labelDefault: 'Columns',
          labelKey: 'columns',
          iconKey: 'columns',
          toolPanel: 'agColumnsToolPanel',
        },
      ],
      defaultToolPanel: '',
    });

  };

  const [refreshTimestamp, setRefreshTimestamp] = useState(Date.now());
  /**
   *
   * @param url
   * @param respKey
   * @return {{respKey, url}}
   */
  const createFetchParams = (url, respKey)=> {
    return{
      url: url,
      respKey: respKey,
    };
  };

  const selectedMultiple = ()=> {
    if(!grid) return false;

    if (grid.api.destroyCalled) {
      return false;
    }
    const selectedNodes = grid.api.getSelectedNodes();
    return selectedNodes && selectedNodes.length >= 2;
  };

  const getSelected = ()=> {
    if(!grid) return null;

    if (grid.api.destroyCalled) {
      return false;
    }

    return grid.api.getSelectedNodes();
  };

  let RenderElement = (children)=><>{children}</>;
  if(blocked) {
    // eslint-disable-next-line react/display-name
    RenderElement = ()=><BlockedPage/>;
  } else if(accessDenied) {
    // eslint-disable-next-line react/display-name
    RenderElement = ()=><AccessDenied/>;
  }



  
  const excelExportParams = {
    processCellCallback: (params) => {
      const colDef = params.column.getColDef();
      if (colDef.valueFormatter) {
        return colDef.valueFormatter({ value: params.value, column: params.column, node: params.node, api: params.api, columnApi: params.columnApi, context: params.context });
      }
      return params.value;
    },
  };

  const exportAllAction = ()=> {
    const api = grid.api;
    const currentPage = api.paginationGetCurrentPage();
    const pageSize = api.paginationGetPageSize();
    const startRow = currentPage * pageSize;
    const endRow = startRow + pageSize;
   
    const currentPageData = [];
    for (let i = startRow; i < endRow; i++) {
      const node = api.getDisplayedRowAtIndex(i);
      if (node) {
        currentPageData.push(node.data);
      }
    }
    tempGrid.api.setRowData(currentPageData);
    setTimeout(() => {
      tempGrid.api.exportDataAsExcel({ fileName: 'current_page_data.xlsx', ...excelExportParams });
      tempGrid.api.setRowData(null);
    }, 0);

  };

  const exportChecked = () => {
    grid.api.exportDataAsExcel({
      onlySelected: true,
      ...excelExportParams,
    });
  };



  const formatFilterModel = (model, columnDefsOrGrid = null) => {
        
    if (model === undefined) return {};

    // grid.api.getFilterModel() returns the filters' own applied model objects, not copies, so
    // the rewrites below would corrupt live filter state (and blank the floating filter text).
    model = cloneDeep(model);

    for(const key of Object.keys(model)) {
      if (key === "cityInspection.requirement") {
        if (model["cityInspection.requirement"]["cityInspection.needsLadder"]) {
          model["cityInspection.needsLadder"] = model["cityInspection.requirement"]["cityInspection.needsLadder"];
        }
        model["cityInspection.requirement"] = model["cityInspection.requirement"]["cityInspection.requirement"];
      }
    }

    const defs = resolveColumnDefsForFilter(columnDefsOrGrid);
    applyConvertToBooleanSetFilters(model, defs);
    applySetFilterValueTransforms(model, defs);

    for(const value of Object.values(model)) {
      if(value.dateFrom) value.dateFrom = Utils.formatLocalTimeToIsoString(value.dateFrom, true);
      if(value.dateTo) value.dateTo = Utils.formatLocalTimeToIsoString(value.dateTo, true);
    }

    return model;
  };

  
  const portalFiltersToAgGridModel = ()=> {
    const agGridFilters = {};
    // if(!additionalFilters.current) return agGridFilters;

    // for (const [field, filter] of Object.entries(additionalFilters.current)) {
    //   if(filter.param === 'empty' || filter.param === 'notEmpty') {
    //     agGridFilters[field] = {
    //       type: filter.param,
    //       filterType: filter.type
    //     };
    //   } else if(filter.value && filter.value.length !== 0){
    //     agGridFilters[field] = {
    //       type: filter.param,
    //       filterType: filter.type,
    //       filter: filter.value
    //     };
    //   }

    // }
    return agGridFilters;

  };

  const getServerSideDataSource =  async(page, limit) => {

    var results = [];

    if(!fetchParams?.url) {
      throw new Error('Grid is still loading, please try the export again in a moment');
    }
    if(!grid?.api || grid.api.destroyCalled) {
      throw new Error('Grid is not available for export');
    }

    const url = isExportingViaEmail.current ? `${base_url.api}export/to-excel` : fetchParams.url;
    const options = fetchParams.options;

    let filterModel = grid.api.getFilterModel();
    filterModel = {...formatFilterModel(filterModel, grid),...portalFiltersToAgGridModel()};
   
    //console.log('global filter');
    //console.log(globalFilters);
    //console.log('search filters');
    //console.log(globalSearchFilters);

    if (Object.entries(globalSearchFilters).length > 0) {
      //console.log('global search filter');
      //console.log(globalSearchFilters);

      filterModel = {...filterModel,  ...globalSearchFilters};
    }
    else { 

      //console.log('global filter 2');
      //console.log(globalFilters);
 
      filterModel = {...filterModel,  ...globalFilters};

    }
    
    //console.log('filter model #2');
    //console.log(filterModel);
 ///  
    let requestParams = {limit: limit, page: page, 
            sortModel: grid.api.request?.sortModel, path: fetchParams.url.replace(base_url.api, '')};

    if(options?.status) {
      requestParams = {...requestParams, status: options.status};
    }

    if(options?.tenantId) {
      requestParams = {...requestParams, tenantId: options.tenantId} 
    }
    if(options?.selectedDate) {
      requestParams = {...requestParams, selectedDate: options.selectedDate} 
    }
    if(options?.companyId) {
      requestParams = {...requestParams, companyId: options.companyId} 
    } 
    if(companyId) {
      requestParams = {...requestParams, companyId: companyId};
    }

    //console.log('global search');
    //console.log(globalSearchFiltersRef.current);

    // if (Object.entries(globalSearchFiltersRef.current).length > 0) {

    //   console.log('send global search');

    //   requestParams = {...requestParams, isGlobalSearch: true};
    // }
    // if (page == 0) {

    //   const visibleColumnFields = grid.columnApi.getAllDisplayedColumns()
    //   .map(col => col.getColDef().field);

    //   requestParams = {...requestParams, path: fetchParams.url.split("/api/")[1], columns: visibleColumnFields, exportData: true};

    // }
    //console.log('request params');
    //console.log(requestParams);

    const authToken = await ApiUtils.getAccessToken();

    //console.log('access token');
    //console.log(authToken);

    if (isExportingViaEmail.current) {

      const visibleColumnFields = grid.columnApi.getAllDisplayedColumns()
        .map(col => col.getColDef().field)
        .filter(Boolean);

      //const visibleColumnFields = [];

      requestParams = {...requestParams, path: fetchParams.url.split("/api/")[1], columns: visibleColumnFields,
                   filterModel: JSON.stringify(filterModel), exportData: true};
      results =  await axios.post(url, requestParams, {headers: {Authorization: authToken}, timeout: EXPORT_REQUEST_TIMEOUT_MS});

    }
    else {
      requestParams = {...requestParams,  filterModel}


      if(fetchParams?.apiSource==='iPermitPro'){
        isExportingViaEmail.current = false;
        results =  await axios.get(url, {params: {...requestParams,...fetchParams.options}, headers: {Authorization: authToken}, timeout: EXPORT_REQUEST_TIMEOUT_MS});
      }
      else {
        results =  await axios.get(url, {params: requestParams, headers: {Authorization: authToken}, timeout: EXPORT_REQUEST_TIMEOUT_MS});
      }
    }

    return results.data.data;
  };

  /** Errors are rethrown: the caller owns the spinner and the user-facing message. */
  const exportToEmail = async() => {

    try {
      isExportingViaEmail.current = true;
      await getServerSideDataSource(0, 0);
    }
    finally {
      isExportingViaEmail.current = false;
    }

  };

  const exportFilteredData = async () => {

    //console.log('inside export filter Data');

    if(!tempGrid?.api || tempGrid.api.destroyCalled) {
      throw new Error('Export grid is not ready, please try again in a moment');
    }
    if(!fetchParams?.respKey) {
      throw new Error('Grid is still loading, please try the export again in a moment');
    }

    /** The email export shares this ref; a stale `true` would POST to export/to-excel and get back a null payload. */
    isExportingViaEmail.current = false;

    let curPage = 1;
    const itemsPerPage = EXPORT_PAGE_SIZE;
    const rowData = [];

    // Every exit is bounded: an empty page, the reported total, the reported page count, or the page cap.
    for (; curPage <= EXPORT_MAX_PAGES; curPage++) {

      const results = await getServerSideDataSource(curPage, itemsPerPage);

      const pageRows = results?.[fetchParams.respKey];
      if (!Array.isArray(pageRows)) {
        throw new Error('Unexpected response while exporting, no records were returned');
      }
      rowData.push(...pageRows);

      if (pageRows.length === 0) break;

      const totalRecords = Number(results?.totalRecords);
      if (Number.isFinite(totalRecords) && itemsPerPage * curPage >= totalRecords) break;

      const pagesCount = Number(results?.pagesCount);
      if (Number.isFinite(pagesCount) && pagesCount > 0 && curPage >= pagesCount) break;
    }

    if (curPage > EXPORT_MAX_PAGES) {
      console.warn(`Export stopped at the ${EXPORT_MAX_PAGES} page cap (${rowData.length} rows); use email export or narrow the filters.`);
    }

    tempGrid.api.setRowData(rowData);
    try {
      tempGrid.api.exportDataAsExcel({ fileName: 'all_filtered_data.xlsx', ...excelExportParams });
    } finally {
      tempGrid.api.setRowData(null);
    }
  };

  const setLoading = useCallback((newValue) => {
    //console.log('set loading ' + newValue);

    setIsLoading(newValue);

  }, []);


  const refreshOnSuccess = (result)=> {

    if(result?.meta?.requestStatus === 'fulfilled' || result?.status === 'success') {
      setRefreshTimestamp(Date.now());
    }
  };

  const refreshGrid = () => {
    setRefreshTimestamp(Date.now());
  };

  const UpdateCustomViewFilterAndSorting = (view) => {

    view.filter = JSON.stringify(grid.api.getFilterModel());
    view.sorting = JSON.stringify(grid.api.getSortModel());
    setCustomViewData(view);
  };

  /**
   * Drops every filter on the grid: the column filters, and the portal level ones
   * layered on top of them. The wrapper forgets the stored filter model on its own,
   * because saving an empty model removes the entry.
   *
   * The `useFilter` side panel is owned by the view, not by this hook, so
   * ListViewHeader clears that alongside this.
   */
  const clearAllFilters = useCallback(() => {
    setGlobalFilters({});
    setGlobalSearchFilters({});

    if(grid?.api && !grid.api.destroyCalled) {
      grid.api.setFilterModel({});
    }
  }, [grid]);

  const changeCurrentView = (view) => {
    if (view) {
      grid.api.setFilterModel(JSON.parse(view.filter));
      grid.api.setSortModel(JSON.parse(view.sorting));
    }
    else {
      grid.api.setFilterModel({});
      grid.api.setSortModel([]);
    }
  };

  return {
    user: user,
    isServiceTitan: company.serviceTitan?.tenantId != undefined && company.serviceTitan?.tenantId != '',
    userRole: role,
    companyId: companyId,
    isParentCompany: company?.childCompanies?.length > 0,
    grid: grid,
    blocked: blocked,
    fetching: fetching,
    pagination: pagination,
    onFetchFail: onFetchFail,
    createFetchParams: createFetchParams,
    setGridToParent: setGridToParent,
    clearAllFilters: clearAllFilters,
    setTempGridToParent: setTempGridToParent,  
    selectedMultipleRows: selectedMultiple,
    setFetching: (s)=>setFetching(s),
    RenderElement: RenderElement,
    setAccessDenied: setAccessDenied,
    getSelected: getSelected,
    exportAll: exportAllAction,
    exportChecked: exportChecked,
    exportFilteredData: exportFilteredData,
    exportToEmail,
    refreshTimestamp: refreshTimestamp,
    refreshOnSuccess: refreshOnSuccess,
    refreshGrid: refreshGrid,
    isLoading: isLoading,
    setIsLoading: (s) =>setIsLoading(s),
    setLoading: setLoading,
    customViewData: customViewData,
    //setCustomViewData: setCustomViewData,
    UpdateCustomViewFilterAndSorting: UpdateCustomViewFilterAndSorting,
    changeCurrentView: changeCurrentView,
    newCustomViewId: newCustomViewId,
    setNewCustomViewId: setNewCustomViewId,
    setGlobalSearchFilters,
    setGlobalFilters,
  };
};
export {useGridViewState};
